import React, { useState, useEffect, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import { MdArticle, MdPrivacyTip, MdGavel, MdContactMail, MdCookie, MdSave } from "react-icons/md";
import { getCmsList, updateCmsContent } from "api/adapters/admin/admin";

const SunEditorWrapper = lazy(() => import("components/editor/SunEditorWrapper"));

const CMS_PAGES = [
  { title: "About Us", icon: MdArticle, description: "Company information and story" },
  { title: "Privacy Policy", icon: MdPrivacyTip, description: "Data privacy and protection" },
  { title: "Terms of Use", icon: MdGavel, description: "Terms and conditions of service" },
  { title: "Cookie Settings", icon: MdCookie, description: "Cookie and tracking preferences" },
  { title: "Contact Us", icon: MdContactMail, description: "Contact information and form" },
];

const CMSManagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("About Us");
  const [editorContent, setEditorContent] = useState({});
  const [originalContent, setOriginalContent] = useState({});
  const [savedToast, setSavedToast] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cmsList"],
    queryFn: getCmsList,
  });

  // Populate editor contents when data loads
  useEffect(() => {
    const list = data?.data?.data ?? [];
    const map = {};
    list.forEach((item) => { map[item.sTitle] = item.sDescription || ""; });
    setEditorContent(map);
    setOriginalContent(map);
  }, [data]);

  const { mutate: doUpdate, isPending: saving } = useMutation({
    mutationFn: (payload) => updateCmsContent(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ["cmsList"] });
      setOriginalContent((prev) => ({ ...prev, [payload.title]: payload.description }));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 3000);
    },
  });  

  const handleSave = () => {
    doUpdate({
      title: activeTab,
      description: editorContent[activeTab] || "",
    });
  };

  const activePage = CMS_PAGES.find((p) => p.title === activeTab);
  const ActiveIcon = activePage?.icon;

  return (
    <div className="flex flex-col gap-5">
      {/* Saved toast */}
      {savedToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl bg-green-500 px-4 py-3 text-sm font-semibold text-white shadow-lg">
          <MdSave className="h-4 w-4" />
          "{activeTab}" saved successfully!
        </div>
      )}

      <div className="flex flex-col gap-5 xl:flex-row">
        {/* Sidebar tabs */}
        <div className="flex flex-row gap-2 xl:flex-col xl:w-64 xl:shrink-0">
          {CMS_PAGES.map(({ title, icon: Icon, description }) => (
            <button
              key={title}
              onClick={() => setActiveTab(title)}
              className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                activeTab === title
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-200 dark:shadow-none"
                  : "bg-white text-navy-700 hover:bg-orange-50 dark:bg-[#111111] dark:text-white dark:hover:bg-[#1a1a1a]"
              }`}
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                activeTab === title ? "bg-white/20" : "bg-orange-100 dark:bg-orange-500/20"
              }`}>
                <Icon className={`h-5 w-5 ${activeTab === title ? "text-white" : "text-orange-500"}`} />
              </div>
              <div className="hidden xl:block min-w-0">
                <p className={`text-sm font-semibold leading-none ${activeTab === title ? "text-white" : "text-navy-700 dark:text-white"}`}>
                  {title}
                </p>
                <p className={`mt-0.5 text-[11px] leading-none ${activeTab === title ? "text-orange-100" : "text-gray-400"}`}>
                  {description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Editor panel */}
        <Card extra="flex-1 p-5 min-w-0">
          {/* Editor header */}
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {ActiveIcon && (
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-500/20">
                  <ActiveIcon className="h-5 w-5 text-orange-500" />
                </div>
              )}
              <div>
                <h2 className="text-base font-bold text-navy-700 dark:text-white">{activeTab}</h2>
                <p className="text-xs text-gray-400">{activePage?.description}</p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || editorContent[activeTab] === originalContent[activeTab]}
              className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-md shadow-orange-200 dark:shadow-none"
            >
              <MdSave className="h-4 w-4" />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>

          {/* SunEditor */}
          {isLoading ? (
            <div className="h-64 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
          ) : (
            <Suspense fallback={<div className="h-64 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />}>
              <SunEditorWrapper
                key={activeTab}
                value={editorContent[activeTab] || ""}
                onChange={(content) =>
                  setEditorContent((prev) => ({ ...prev, [activeTab]: content }))
                }
                placeholder={`Enter ${activeTab} content here...`}
              />
            </Suspense>
          )}

          <p className="mt-3 text-xs text-gray-400">
            Saved content is shown on the user web app (and related clients) under{" "}
            <strong className="text-orange-500">{activeTab}</strong>.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default CMSManagement;
